
import axios, { CancelTokenSource } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { AppState } from '../types/redux/store';
import { debounce } from 'lodash';
import { reportError, ErrorType } from './errorReporting/errorReporting';
import { BASE_URL } from './apiConstants';
import { sanitizeNaNInfinity } from './helpers/debug/api.debuggers';


interface MakeJsonCallProps {
  [key: string]: any;
  url: string;
}

interface MakeJsonCallAction {
  startAction: any;
  successAction: any;
  failureAction: any;
  props: MakeJsonCallProps;
  cancelSource: CancelTokenSource;
}

// Helper function to add interceptors to an axios instance
function addSanitizationInterceptor(instance: ReturnType<typeof axios.create>) {
  instance.interceptors.response.use(
    (response) => {
      // Sanitize NaN/Infinity values in string responses
      if (typeof response.data === 'string') {
        try {
          const { data, wasSanitized, sanitizedEntries } = sanitizeNaNInfinity(response.data);
          response.data = data;

          // Report if sanitization occurred
          if (wasSanitized) {
            const url = response.config?.url || 'unknown';
            debouncedReportSanitization(
              `NaN/Infinity values found and sanitized in API response - Endpoint: ${url}`,
              {
                url: url,
                dataType: 'success_response',
                wasSanitized: true,
                sanitizedEntries,
              },
            );
          }
        } catch (e) {
          const url = response.config?.url || 'unknown';
          // If parsing fails, report error and keep original data
          debouncedReportSanitization(
            `Failed to sanitize/parse NaN/Infinity values in API response - Endpoint: ${url}`,
            e,
            {
              url: url,
              dataType: 'success_response',
              wasSanitized: false,
            },
          );
        }
      }
      return response;
    },
  );
}

const client = () => {
  const instance = axios.create({
    withCredentials: true,
    baseURL: BASE_URL,
  });
  // Add sanitization interceptor to all client instances
  addSanitizationInterceptor(instance);
  return instance;
};

export const AxiosInstance = client();

const debouncedReportSanitization = debounce(
  (
    message: string,
    errorOrContext?: any,
    context?: { url?: string; dataType?: string; wasSanitized?: boolean },
  ) => {
    // If errorOrContext is an Error object, use it; otherwise create a simple error
    // Use a generic error message to avoid duplication since the main message already contains details

    const actualError = errorOrContext instanceof Error 
      ? errorOrContext 
      : new Error('Sanitization event occurred');
    const actualContext = errorOrContext instanceof Error 
      ? context 
      : (errorOrContext || context);
    
    reportError(
      message,
      actualError,
      ErrorType.DATA_PROCESSING_ERROR,
      actualContext,
    );
  },
  600000, // 10 minutes - very large debounce to send only once
  { leading: true, trailing: false }, // Fire immediately on first occurrence, ignore subsequent ones
);
// The general data fetch function. This is used for all data fetches, allowing us to cache data in redux and prevent unnecessary calls.

// This issue with this hook is if we are on a raidr route we need to use the raidrClient, but if we are on a non-raidr route we need to use the client.
export const makeGeneralDataFetch =
  ({
    startAction,
    successAction,
    failureAction,
    cancelSource,
    props,
  }: MakeJsonCallAction): ThunkAction<void, AppState, null, any> =>
  (dispatch) => {
    dispatch(startAction(props));
    const requestClient = client();
    // this is a quick hack to use the local config if we're not in production
    if (!props.returnLocalConfig) {
      // if we have a formData object, we're doing a post request
      if (props.formData) {
        requestClient
          .post(props.url, props.formData, {
            cancelToken: cancelSource.token,
            headers: {
              'content-type': 'multipart/form-data',
            },
          })
          .then((innerResponse: any) => {
            const { data } = innerResponse;
            setTimeout(() => {
              dispatch(successAction({ ...props, data }));
            }, 1);
          })
          .catch((error: any) => {
            dispatch(
              failureAction({
                ...props,
                errorMessage: error,
              }),
            );
          });
        // otherwise we're doing a get request
      } else {
        const baseURL = requestClient.defaults.baseURL;

        // Log the full URL for the POST request
        requestClient
          .get(props.url, { cancelToken: cancelSource.token })
          .then((innerResponse: any) => {
            const { data } = innerResponse;
            setTimeout(() => {
              dispatch(successAction({ ...props, data }));
            }, 1);
          })
          .catch((error: any) => {
            dispatch(
              failureAction({
                ...props,
                errorMessage: error,
              }),
            );
          });
      }
    } else {
      try {
        import(`../client_configurations/config/${props.returnLocalConfig}.json`)
          .then((module) => {
            return module.default;
          })
          .then((data) => {
            dispatch(successAction({ ...props, data }));
          })
          .catch((err) => {
            dispatch(
              failureAction({
                ...props,
                errorMessage: err,
              }),
            );
          });
      } catch (err) {
        dispatch(
          failureAction({
            ...props,
            errorMessage: err,
          }),
        );
      }
    }
  };

export default client;